import { useState } from 'react';

import { Box, Button, createStyles, makeStyles } from '@material-ui/core';
import { AiOutlinePlus } from 'react-icons/ai';

import { AppSearchInput } from '@vyce/core/src/components/inputs';
import { FormDoc, User } from '@vyce/core/src/types';

import { FormRecipientsTable } from './components/FormRecipientsTable';
import { FormInviteRecipientsDialog } from './components/FormInviteRecipientsDialog';

type RecipientsFilter = 'recipients' | 'outstanding' | 'responses';

const STATUS_OPTIONS: { value: RecipientsFilter; label: string }[] = [
  { value: 'recipients', label: 'All' },
  { value: 'outstanding', label: 'Outstanding' },
  { value: 'responses', label: 'Completed' },
];

const useStyles = makeStyles(theme =>
  createStyles({
    container: {
      width: '90%',
      margin: '0 auto',
      marginTop: theme.spacing(3),
    },
    chipsRow: {
      display: 'flex',
      gridGap: 8,
      flexWrap: 'wrap',
      marginBottom: theme.spacing(2),
    },
    chip: {
      cursor: 'pointer',
      padding: `${theme.spacing(0.75)}px ${theme.spacing(2)}px`,
      borderRadius: 999,
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.primary,
      fontSize: 13,
      fontWeight: 500,
      lineHeight: 1.4,
      transition: 'background-color 120ms ease, border-color 120ms ease, color 120ms ease',
      userSelect: 'none',
      '&:hover': {
        borderColor: theme.palette.primary.main,
        color: theme.palette.primary.main,
      },
    },
    chipActive: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.getContrastText(theme.palette.primary.main),
      borderColor: theme.palette.primary.main,
      '&:hover': {
        backgroundColor: theme.palette.primary.main,
        borderColor: theme.palette.primary.main,
        color: theme.palette.getContrastText(theme.palette.primary.dark),
      },
    },
    toolbar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gridGap: 16,
      flexWrap: 'wrap',
      marginBottom: theme.spacing(2),
    },
  })
);

export const FormRecipients: React.FC<{
  form: FormDoc | null;
  user: User;
  companyId: string;
}> = ({ form, user, companyId }) => {
  const classes = useStyles();
  const [reloadKey, setReloadKey] = useState(0);
  const [filter, setFilter] = useState<RecipientsFilter>('recipients');
  const [search, setSearch] = useState('');
  const [isInviteOpen, setIsInviteOpen] = useState(false);

  const formId = form?.id;

  return (
    <Box className={classes.container}>
      <Box className={classes.chipsRow} role="tablist" aria-label="Recipients status filter">
        {STATUS_OPTIONS.map(option => {
          const isActive = option.value === filter;
          return (
            <Box
              key={option.value}
              role="tab"
              aria-selected={isActive}
              tabIndex={0}
              className={`${classes.chip} ${isActive ? classes.chipActive : ''}`}
              onClick={() => setFilter(option.value)}
              onKeyDown={event => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  setFilter(option.value);
                }
              }}>
              {option.label}
            </Box>
          );
        })}
      </Box>

      <Box className={classes.toolbar}>
        <AppSearchInput
          placeholder="Search recipients"
          value={search}
          onChange={e => setSearch(e.target.value)}
          isSmall
          smallRounding
          isBorder
          expanded
        />

        <Button
          onClick={() => setIsInviteOpen(true)}
          startIcon={<AiOutlinePlus />}
          variant="contained"
          color="primary"
          disabled={!formId}>
          Invite
        </Button>
      </Box>

      {formId && (
        <FormRecipientsTable
          type={filter}
          companyId={companyId}
          formId={formId}
          user={user}
          reloadKey={reloadKey}
          height="calc(100vh - 360px)"
          searchValue={search}
          hideSearchInput
        />
      )}

      <FormInviteRecipientsDialog
        open={isInviteOpen}
        onClose={() => setIsInviteOpen(false)}
        form={form}
        user={user}
        companyId={companyId}
        onSent={() => setReloadKey(key => key + 1)}
      />
    </Box>
  );
};
